import { Button } from "@/ingot";
import { useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { UploadErrorAlert } from "@/components/upload/UploadErrorAlert";
import { useUploadFiles } from "@/components/upload/useUploadFiles";
import { cx } from "@/lib/utils";

interface UploadDropzoneProps {
  /** Maximum allowed size in bytes per individual file. */
  maxBytes?: number;
  /** Maximum number of files allowed per drop. */
  maxFiles?: number;
  /** Maximum aggregate size in bytes across all dropped files. */
  maxTotalBytes?: number;
  /** Disable interaction — used while an upload is in-flight. */
  disabled?: boolean;
  /** Called with the chosen files after client-side validation. The
   * array is always non-empty when fired; length 1 is the common
   * path, 2-N means multi-DXF batch upload. */
  onFilesChosen: (files: File[]) => void;
  /** Server-side error message to surface under the drop target. */
  errorMessage?: string | null;
  /** External status label ("analyzing…") shown inside the target. */
  statusLabel?: string | null;
  /** Allowed file extensions including the leading dot (lowercase),
   *  e.g. ``[".dxf"]`` for laser. Drives both the native file picker's
   *  ``accept`` attribute and the client-side extension check on
   *  drop. Defaults to ``[".dxf"]`` so legacy callers (Landing's
   *  fallback dropzone) keep working without an explicit prop. */
  acceptedFileTypes?: readonly string[];
  /** Headline override — replaces the generic ``upload.title`` copy.
   *  The Landing process switch passes the per-process variant
   *  ("Přetáhněte sem soubor pro {process}") so the dropzone reads
   *  what will be computed before the file is dropped. */
  title?: string | null;
  /** Display-only extension list for the hint row; falls back to
   *  ``acceptedFileTypes``. Lets the caller keep the validation gate
   *  on the tenant-wide union (so dropping another process's file
   *  reaches the auto-switch resolution) while messaging only the
   *  selected process's formats. */
  hintFileTypes?: readonly string[];
  /** When provided, renders a secondary "try sample file" CTA next
   *  to the primary button. Tapping it calls this handler — the
   *  caller is responsible for sourcing the file (e.g. fetching a
   *  static demo from /public) and feeding it back through
   *  ``onFilesChosen``. */
  onSampleFile?: () => void;
  /** When set, renders an "Analýza < N s" speed pill in the feature
   *  row. ``null`` (the default) hides the pill entirely — used on
   *  tenants whose public-entry modules don't declare a calc-time
   *  SLA so we don't render a fake promise. */
  slaCalcSeconds?: number | null;
}

const DEFAULT_MAX_BYTES = 20 * 1024 * 1024;
const DEFAULT_MAX_FILES = 25;
const DEFAULT_MAX_TOTAL_BYTES = 100 * 1024 * 1024;

/**
 * Drag-and-drop + click-to-choose upload target.
 *
 * The component is accessibility-first: the explicit "choose file"
 * button is the primary keyboard / pointer affordance and the whole
 * panel acts as a drag-and-drop target.
 *
 * Client-side validation lives here — it's purely UX (the server
 * re-validates every rule), but surfacing errors instantly beats
 * waiting for a round-trip.
 */
const DEFAULT_ACCEPTED: readonly string[] = [".dxf"];

export function UploadDropzone({
  maxBytes = DEFAULT_MAX_BYTES,
  maxFiles = DEFAULT_MAX_FILES,
  maxTotalBytes = DEFAULT_MAX_TOTAL_BYTES,
  disabled = false,
  onFilesChosen,
  errorMessage,
  statusLabel,
  acceptedFileTypes = DEFAULT_ACCEPTED,
  title = null,
  hintFileTypes,
  onSampleFile,
  slaCalcSeconds = null,
}: UploadDropzoneProps): JSX.Element {
  const { t } = useTranslation("quote");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const { handleFiles, localError, maxMb } = useUploadFiles({
    maxBytes,
    maxFiles,
    maxTotalBytes,
    acceptedFileTypes,
    disabled,
    onFilesChosen,
  });

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset so selecting the same file twice re-fires.
    e.target.value = "";
  };

  const displayedError = errorMessage ?? localError;
  const acceptedLabel = (hintFileTypes ?? acceptedFileTypes).join(", ");

  return (
    <div>
      <div
        aria-disabled={disabled}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cx(
          "st-drop rounded-2xl border-2 border-dashed px-6 py-10 text-center transition sm:px-10 sm:py-12",
          dragOver ? "border-ink bg-surface-2" : "border-border-strong bg-surface",
          disabled && "pointer-events-none opacity-60",
          displayedError && "border-danger",
        )}
        data-testid="upload-dropzone"
      >
        <div
          aria-hidden="true"
          className="st-drop-ic mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-bg text-accent-ink"
        >
          <svg
            className="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.75}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 7.5 7.5 12M12 7.5v12"
            />
          </svg>
        </div>

        <h3 className="st-drop-t mt-5 text-lg font-semibold text-ink sm:text-xl">
          {statusLabel ??
            (dragOver ? t("upload.dropActive") : title ?? t("upload.title"))}
        </h3>
        {!statusLabel && (
          <p className="st-drop-h mt-2 text-sm text-ink-3">
            {t("upload.hint", { accepted: acceptedLabel, maxMb })}
          </p>
        )}

        <div className="st-drop-act mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
          <Button
            variant="primary"
            size="lg"
            className="st-btn"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            leadingIcon={
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.75}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                />
              </svg>
            }
            data-testid="upload-pick-file"
          >
            {t("upload.pickCta")}
          </Button>
          {onSampleFile && (
            <Button
              variant="secondary"
              size="lg"
              className="st-btn st-btn-s"
              disabled={disabled}
              onClick={onSampleFile}
              data-testid="upload-sample-file"
            >
              {t("upload.sampleCta")} →
            </Button>
          )}
        </div>

        <hr className="st-drop-hr mx-auto mt-8 max-w-xl border-t border-dashed border-border" />

        <ul className="st-drop-foot mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-ink-2 sm:text-sm">
          {slaCalcSeconds != null && (
            <>
              <li className="flex items-center gap-2">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent" />
                {t("upload.featureSpeedDynamic", {
                  seconds: slaCalcSeconds,
                  defaultValue: `Analýza < ${slaCalcSeconds} s`,
                })}
              </li>
              <li aria-hidden="true" className="text-ink-4">|</li>
            </>
          )}
          <li>{t("upload.featureEu")}</li>
          <li aria-hidden="true" className="text-ink-4">|</li>
          <li>{t("upload.featureNoAccount")}</li>
        </ul>

        <input
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes.join(",")}
          multiple
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
          data-testid="upload-input"
        />
      </div>

      {displayedError && (
        <UploadErrorAlert message={displayedError} className="mt-3" />
      )}
    </div>
  );
}
